import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { StatusBadge } from "@/components/status/status-badge";
import { api } from "@/lib/api";
import {
  FilterField,
  FilterToolbar,
  PaginationBar,
  SearchInput,
  formatUnix,
  selectClassName,
  userStatusTone,
} from "./shared";

type UserRow = {
  id: string;
  email: string;
  displayName: string;
  isPlatformAdmin: boolean;
  status: string;
  statusReason?: string | null;
  memberships: number;
  accountId?: string | null;
  accountName?: string | null;
  role?: string | null;
  createdAt: number;
};

type UserDetail = {
  user: {
    id: string;
    email: string;
    displayName: string;
    isPlatformAdmin: boolean;
    createdAt: number;
    status: string;
    statusReason: string | null;
    statusChangedAt: number | null;
    statusChangedBy: string | null;
  };
  membership: {
    accountId: string;
    accountName: string;
    role: string;
  } | null;
  recentAudit: Array<Record<string, unknown>>;
  resourceAudit: Array<Record<string, unknown>>;
};

export function SuperAdminUsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [qDraft, setQDraft] = useState(searchParams.get("q") ?? "");
  const [q, setQ] = useState(searchParams.get("q") ?? "");
  const [status, setStatus] = useState(searchParams.get("status") ?? "");
  const [page, setPage] = useState(Number(searchParams.get("page") ?? 1) || 1);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    params.set("page", String(page));
    params.set("limit", String(limit));
    void api<{ users: UserRow[]; total: number }>(
      `/api/platform/users?${params}`,
    )
      .then((r) => {
        setUsers(r.users ?? []);
        setTotal(r.total ?? 0);
      })
      .catch(() => {
        setUsers([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [q, status, page, limit]);

  function applyFilters() {
    setQ(qDraft.trim());
    setPage(1);
    const next = new URLSearchParams();
    if (qDraft.trim()) next.set("q", qDraft.trim());
    if (status) next.set("status", status);
    setSearchParams(next);
  }

  function clearFilters() {
    setQDraft("");
    setQ("");
    setStatus("");
    setPage(1);
    setSearchParams({});
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-rs-ink">Users</h1>
        <p className="text-sm text-rs-ink-muted">
          Search, filter, and manage platform users.
        </p>
      </div>
      <FilterToolbar>
        <FilterField
          id="sa-users-q"
          label="Search"
          className="min-w-[14rem] flex-[2] space-y-1"
        >
          <SearchInput
            id="sa-users-q"
            value={qDraft}
            onChange={setQDraft}
            placeholder="Email or display name"
          />
        </FilterField>
        <FilterField
          id="sa-users-status"
          label="Status"
          className="min-w-[10rem] space-y-1"
        >
          <select
            id="sa-users-status"
            className={selectClassName}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Any</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
            <option value="suspended">Suspended</option>
          </select>
        </FilterField>
        <Button type="button" variant="secondary" onClick={applyFilters}>
          Search
        </Button>
        <Button type="button" variant="ghost" onClick={clearFilters}>
          Clear
        </Button>
      </FilterToolbar>
      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          <Panel>
            <ul className="divide-y divide-rs-border">
              {users.map((u) => (
                <li key={u.id}>
                  <Link
                    to={`/superadmin/users/${u.id}`}
                    className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm hover:bg-rs-muted"
                  >
                    <div>
                      <p className="font-medium text-rs-ink">{u.displayName}</p>
                      <p className="text-rs-ink-muted">{u.email}</p>
                      {u.accountName ? (
                        <p className="text-xs text-rs-ink-subtle">
                          {u.accountName}
                          {u.role ? ` · ${u.role}` : ""}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {u.isPlatformAdmin ? (
                        <StatusBadge tone="warning">Super Admin</StatusBadge>
                      ) : null}
                      <StatusBadge tone={userStatusTone(u.status)}>
                        {u.status}
                      </StatusBadge>
                      <span className="text-rs-ink-muted">
                        {formatUnix(u.createdAt)}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
              {users.length === 0 ? (
                <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
                  No users found.
                </li>
              ) : null}
            </ul>
          </Panel>
          <PaginationBar
            page={page}
            limit={limit}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

export function SuperAdminUserDetailPage() {
  const { userId = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [statusAction, setStatusAction] = useState<
    "disable" | "suspend" | "enable" | "restore" | null
  >(null);
  const [statusReason, setStatusReason] = useState("");
  const [roleDraft, setRoleDraft] = useState<"admin" | "member">("member");
  const [roleConfirm, setRoleConfirm] = useState(false);
  const [impersonateConfirm, setImpersonateConfirm] = useState(false);
  const [platformAdminConfirm, setPlatformAdminConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const r = await api<UserDetail>(
      `/api/platform/users/${encodeURIComponent(userId)}`,
    );
    setData(r);
    if (r.membership?.role === "admin" || r.membership?.role === "member") {
      setRoleDraft(r.membership.role);
    }
  }

  useEffect(() => {
    setLoading(true);
    void refresh()
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load by userId
  }, [userId]);

  if (loading) return <PageSkeleton />;
  if (!data) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold text-rs-ink">User Not Found</h1>
        <Button
          variant="secondary"
          onClick={() => navigate("/superadmin/users")}
        >
          Back to Users
        </Button>
      </div>
    );
  }

  const { user, membership } = data;
  const canChangeStatus = !user.isPlatformAdmin;
  const canImpersonate = !user.isPlatformAdmin && user.status === "active";
  const canChangeRole =
    membership != null && membership.role !== "owner" && !user.isPlatformAdmin;
  const canTogglePlatformAdmin = true;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm text-rs-ink-muted">
            <Link
              className="text-rs-accent hover:underline"
              to="/superadmin/users"
            >
              Users
            </Link>
            {" / "}
            {user.displayName}
          </p>
          <h1 className="text-xl font-semibold text-rs-ink">
            {user.displayName}
          </h1>
          <p className="text-sm text-rs-ink-muted">{user.email}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canImpersonate ? (
            <Button type="button" onClick={() => setImpersonateConfirm(true)}>
              Impersonate
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Panel className="p-4">
          <p className="text-sm text-rs-ink-muted">Status</p>
          <div className="mt-1 flex items-center gap-2">
            <StatusBadge tone={userStatusTone(user.status)}>
              {user.status}
            </StatusBadge>
            {user.isPlatformAdmin ? (
              <StatusBadge tone="warning">Super Admin</StatusBadge>
            ) : null}
          </div>
          {user.statusReason ? (
            <p className="mt-2 text-xs text-rs-ink-muted">
              {user.statusReason}
            </p>
          ) : null}
        </Panel>
        <Panel className="p-4">
          <p className="text-sm text-rs-ink-muted">Account</p>
          {membership ? (
            <>
              <Link
                className="mt-1 block font-medium text-rs-accent hover:underline"
                to={`/superadmin/accounts/${membership.accountId}`}
              >
                {membership.accountName}
              </Link>
              <p className="text-sm text-rs-ink-muted capitalize">
                {membership.role}
              </p>
            </>
          ) : (
            <p className="mt-1 text-rs-ink-muted">No membership</p>
          )}
        </Panel>
        <Panel className="p-4">
          <p className="text-sm text-rs-ink-muted">Created</p>
          <p className="mt-1 font-medium text-rs-ink">
            {formatUnix(user.createdAt)}
          </p>
        </Panel>
      </div>

      {canTogglePlatformAdmin ? (
        <Panel className="space-y-3 p-5">
          <h2 className="font-semibold text-rs-ink">Super Admin Access</h2>
          <p className="text-sm text-rs-ink-muted">
            Grant or revoke platform Super Admin via the authoritative{" "}
            <code className="text-xs">is_platform_admin</code> flag. You cannot
            change your own flag.
          </p>
          <Button
            type="button"
            variant={user.isPlatformAdmin ? "danger" : "secondary"}
            onClick={() => setPlatformAdminConfirm(true)}
          >
            {user.isPlatformAdmin ? "Revoke Super Admin" : "Grant Super Admin"}
          </Button>
        </Panel>
      ) : null}

      {canChangeStatus ? (
        <Panel className="space-y-3 p-5">
          <h2 className="font-semibold text-rs-ink">Account Status</h2>
          <p className="text-sm text-rs-ink-muted">
            Disable or suspend blocks login. Provide a reason for those actions.
          </p>
          <div className="flex flex-wrap gap-2">
            {user.status !== "active" ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStatusAction("restore")}
              >
                Restore
              </Button>
            ) : null}
            {user.status === "active" ? (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setStatusAction("disable")}
                >
                  Disable
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => setStatusAction("suspend")}
                >
                  Suspend
                </Button>
              </>
            ) : null}
            {user.status === "suspended" ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStatusAction("enable")}
              >
                Enable
              </Button>
            ) : null}
          </div>
        </Panel>
      ) : null}

      {canChangeRole ? (
        <Panel className="space-y-3 p-5">
          <h2 className="font-semibold text-rs-ink">Workspace Role</h2>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[10rem] space-y-1">
              <label
                className="text-sm text-rs-ink-muted"
                htmlFor="sa-user-role"
              >
                Role
              </label>
              <select
                id="sa-user-role"
                className={selectClassName}
                value={roleDraft}
                onChange={(e) =>
                  setRoleDraft(e.target.value as "admin" | "member")
                }
              >
                <option value="admin">Admin</option>
                <option value="member">Member</option>
              </select>
            </div>
            <Button
              type="button"
              disabled={roleDraft === membership?.role}
              onClick={() => setRoleConfirm(true)}
            >
              Save Role
            </Button>
          </div>
        </Panel>
      ) : null}

      <Panel>
        <PanelHeader
          title="Recent Activity"
          description="Audit events involving this user"
        />
        <ul className="divide-y divide-rs-border">
          {[...data.resourceAudit, ...data.recentAudit]
            .slice(0, 20)
            .map((e, i) => (
              <li key={String(e.id ?? i)} className="px-5 py-3 text-sm">
                <p className="font-medium text-rs-ink">
                  {String(e.action ?? "event")}
                </p>
                <p className="text-rs-ink-muted">
                  {formatUnix(Number(e.createdAt))}
                </p>
              </li>
            ))}
          {data.resourceAudit.length === 0 && data.recentAudit.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
              No recent audit events.
            </li>
          ) : null}
        </ul>
      </Panel>

      {message ? <p className="text-sm text-rs-ink-muted">{message}</p> : null}

      <ConfirmDialog
        open={statusAction != null}
        onOpenChange={(open) => {
          if (!open) {
            setStatusAction(null);
            setStatusReason("");
          }
        }}
        title={
          statusAction === "suspend"
            ? "Suspend User"
            : statusAction === "disable"
              ? "Disable User"
              : statusAction === "restore"
                ? "Restore User"
                : "Enable User"
        }
        description={
          statusAction === "suspend" || statusAction === "disable"
            ? "This user will be blocked from signing in. Enter a reason for the audit log."
            : "Restore this user to active status so they can sign in again."
        }
        impact={
          statusAction === "suspend" || statusAction === "disable" ? (
            <Input
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              placeholder="Reason (required)"
              aria-label="Status Reason"
            />
          ) : (
            <p>{user.displayName} → active</p>
          )
        }
        confirmLabel={
          statusAction === "suspend"
            ? "Suspend User"
            : statusAction === "disable"
              ? "Disable User"
              : "Restore User"
        }
        dangerous={statusAction === "suspend" || statusAction === "disable"}
        busy={busy}
        onConfirm={async () => {
          if (!statusAction) return;
          if (
            (statusAction === "suspend" || statusAction === "disable") &&
            !statusReason.trim()
          ) {
            setMessage("Reason is required.");
            return;
          }
          setBusy(true);
          try {
            await api(
              `/api/platform/users/${encodeURIComponent(userId)}/status`,
              {
                method: "POST",
                body: JSON.stringify({
                  action: statusAction,
                  reason: statusReason.trim() || undefined,
                }),
              },
            );
            setStatusAction(null);
            setStatusReason("");
            setMessage("Status updated.");
            await refresh();
          } catch (err) {
            setMessage(
              err instanceof Error ? err.message : "Status update failed",
            );
          } finally {
            setBusy(false);
          }
        }}
      />

      <ConfirmDialog
        open={roleConfirm}
        onOpenChange={setRoleConfirm}
        title="Change Workspace Role"
        description={`Update ${user.displayName}'s role in ${membership?.accountName ?? "their workspace"}.`}
        impact={
          <p>
            {membership?.role} → {roleDraft}
          </p>
        }
        confirmLabel="Save Role"
        busy={busy}
        onConfirm={async () => {
          setBusy(true);
          try {
            await api(
              `/api/platform/users/${encodeURIComponent(userId)}/membership`,
              {
                method: "PATCH",
                body: JSON.stringify({ role: roleDraft }),
              },
            );
            setRoleConfirm(false);
            setMessage("Role updated.");
            await refresh();
          } catch (err) {
            setMessage(
              err instanceof Error ? err.message : "Role update failed",
            );
          } finally {
            setBusy(false);
          }
        }}
      />

      <ConfirmDialog
        open={platformAdminConfirm}
        onOpenChange={setPlatformAdminConfirm}
        title={
          user.isPlatformAdmin ? "Revoke Super Admin" : "Grant Super Admin"
        }
        description={
          user.isPlatformAdmin
            ? "This user will lose access to the Super Admin control plane."
            : "This user will gain full Super Admin access to the control plane."
        }
        impact={
          <p>
            {user.displayName}:{" "}
            {user.isPlatformAdmin
              ? "Super Admin → standard user"
              : "standard user → Super Admin"}
          </p>
        }
        confirmLabel={
          user.isPlatformAdmin ? "Revoke Super Admin" : "Grant Super Admin"
        }
        dangerous={user.isPlatformAdmin}
        busy={busy}
        onConfirm={async () => {
          setBusy(true);
          try {
            await api(`/api/platform/users/${encodeURIComponent(userId)}`, {
              method: "PATCH",
              body: JSON.stringify({ isPlatformAdmin: !user.isPlatformAdmin }),
            });
            setPlatformAdminConfirm(false);
            setMessage(
              user.isPlatformAdmin
                ? "Super Admin revoked."
                : "Super Admin granted.",
            );
            await refresh();
          } catch (err) {
            setMessage(
              err instanceof Error
                ? err.message
                : "Could not update Super Admin",
            );
          } finally {
            setBusy(false);
          }
        }}
      />

      <ConfirmDialog
        open={impersonateConfirm}
        onOpenChange={setImpersonateConfirm}
        title="Impersonate User"
        description="You will operate the workspace as this user. Super Admin APIs are blocked until you exit impersonation."
        impact={
          <p>
            Target: {user.displayName} ({user.email})
          </p>
        }
        confirmLabel="Start Impersonation"
        dangerous
        busy={busy}
        onConfirm={async () => {
          setBusy(true);
          try {
            await api(
              `/api/platform/users/${encodeURIComponent(userId)}/impersonate`,
              {
                method: "POST",
              },
            );
            setImpersonateConfirm(false);
            // Full auth refresh so workspace routes reflect the target user.
            window.location.assign("/");
          } catch (err) {
            setMessage(
              err instanceof Error ? err.message : "Impersonation failed",
            );
            setBusy(false);
          }
        }}
      />
    </div>
  );
}
